import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/user.service';
import { ONBOARDING_STEPS, CATEGORIES, INTERESTS } from '../../constants/onboarding';
import WelcomeStep from './steps/WelcomeStep';
import ProfileStep from './steps/ProfileStep';
import InterestsStep from './steps/InterestsStep';
import GoalsStep from './steps/GoalsStep';
import PreferencesStep from './steps/PreferencesStep';
import CompleteStep from './steps/CompleteStep';
import PricingStep from './steps/PricingStep';
import './Onboarding.css';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { updateProfile } from 'firebase/auth';

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(ONBOARDING_STEPS.WELCOME);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { user, setIsOnboarded } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    profile: {
      displayName: '',
      bio: '',
      avatar: null,
      birthday: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    interests: {
      categories: [],
      specificInterests: []
    },
    goals: {
      primary: '',
      timeline: '3_months',
      milestones: []
    },
    preferences: {
      notifications: true,
      emailUpdates: true,
      theme: 'system',
      privacyLevel: 'balanced'
    }
  });

  useEffect(() => {
    if (user?.displayName) {
      setFormData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          displayName: user.displayName
        }
      }));
    }
  }, [user]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const getNextStep = (currentStep) => {
    const steps = Object.values(ONBOARDING_STEPS);
    const currentIndex = steps.indexOf(currentStep);
    return currentIndex < steps.length - 1 ? steps[currentIndex + 1] : currentStep;
  };

  const handleNext = async (currentStep) => {
    if (currentStep === ONBOARDING_STEPS.COMPLETE) {
      await handleSubmit();
      return;
    }

    const nextStep = getNextStep(currentStep);
    setCurrentStep(nextStep);
  };

  const handleBack = () => {
    const steps = Object.values(ONBOARDING_STEPS);
    const currentIndex = steps.indexOf(currentStep);
    
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case ONBOARDING_STEPS.PROFILE:
        return formData.profile.displayName.trim() !== '' && formData.profile.birthday;
      case ONBOARDING_STEPS.INTERESTS:
        return formData.interests.categories.length > 0;
      case ONBOARDING_STEPS.GOALS:
        return formData.goals.primary.trim() !== '';
      default:
        return true;
    }
  };

  // Utility function to remove undefined values and clean objects
  const removeUndefined = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return obj ?? null;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefined(item)).filter(item => item != null);
    }

    const clean = {};
    Object.entries(obj).forEach(([key, value]) => {
      const cleanValue = removeUndefined(value);
      if (cleanValue != null) {
        clean[key] = cleanValue;
      }
    });
    return clean;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      // Create base data structure with default values for all fields
      const baseData = removeUndefined({
        profile: {
          displayName: formData.profile.displayName || '',
          bio: formData.profile.bio || '',
          timezone: formData.profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          birthday: formData.profile.birthday ? new Date(formData.profile.birthday).toISOString() : null,
          avatarUrl: formData.profile.avatar?.url || null,
          avatarPath: formData.profile.avatar?.path || null
        },
        interests: {
          categories: Array.isArray(formData.interests.categories) ? formData.interests.categories : [],
          specificInterests: Array.isArray(formData.interests.specificInterests) ? formData.interests.specificInterests : []
        },
        goals: {
          primary: formData.goals.primary || '',
          timeline: formData.goals.timeline || '3_months',
          milestones: Array.isArray(formData.goals.milestones) ? formData.goals.milestones : []
        },
        preferences: {
          notifications: Boolean(formData.preferences.notifications),
          emailUpdates: Boolean(formData.preferences.emailUpdates),
          theme: formData.preferences.theme || 'system',
          privacyLevel: formData.preferences.privacyLevel || 'balanced'
        },
        hasCompletedOnboarding: true,
        updatedAt: new Date().toISOString()
      });

      // Update Firebase Auth profile if avatar exists
      if (formData.profile.avatar?.url) {
        await updateProfile(user, {
          photoURL: formData.profile.avatar.url,
          displayName: baseData.profile.displayName
        });
      }

      // Save to Firestore
      await updateDoc(doc(db, 'users', user.uid), baseData);

      setIsOnboarded(true);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Error saving onboarding data:', err);
      setError('Failed to complete onboarding. Please try again.');
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    const commonProps = {
      formData,
      onInputChange: handleInputChange,
      onNext: () => handleNext(currentStep),
      onBack: handleBack,
      isValid: validateStep(currentStep)
    };

    switch (currentStep) {
      case ONBOARDING_STEPS.WELCOME:
        return <WelcomeStep {...commonProps} />;
      case ONBOARDING_STEPS.PROFILE:
        return <ProfileStep {...commonProps} />;
      case ONBOARDING_STEPS.INTERESTS:
        return <InterestsStep {...commonProps} />;
      case ONBOARDING_STEPS.GOALS:
        return <GoalsStep {...commonProps} />;
      case ONBOARDING_STEPS.PREFERENCES:
        return <PreferencesStep {...commonProps} />;
      case ONBOARDING_STEPS.PRICING:
        return <PricingStep {...commonProps} />;
      case ONBOARDING_STEPS.COMPLETE:
        return <CompleteStep {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-container">
      {error && <div className="onboarding-error">{error}</div>}
      <div className="onboarding-progress">
        {Object.values(ONBOARDING_STEPS).map((step, index) => (
          <div 
            key={step}
            className={`progress-step ${currentStep === step ? 'active' : ''} 
              ${Object.values(ONBOARDING_STEPS).indexOf(currentStep) > index ? 'completed' : ''}`}
          />
        ))}
      </div>
      {renderStep()}
      {submitting && (
        <div className="onboarding-overlay">
          <div className="onboarding-spinner">
            <span>Completing setup...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding; 